import React, { useState, useEffect } from "react";
import Modal from "../../../../shared/components/dashboard-layout/modalComponent";
import "./compose-email-modal.scss";

const ComposeEmailModal = ({ onClose, notification, isOpen, onSendEmail, errorModal: { setShowErrorModal, setErrorModalHeaderAndMessage } }) => {
  // State management
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const [isSending, setIsSending] = useState(false);

  // Effects
  useEffect(() => {
    if (notification && isOpen) {
      const prefilledData = generateEmailContent(notification);
      setEmailData(prefilledData);
    }
  }, [notification, isOpen]);

  // Utility functions
  const generateEmailContent = (notification) => {
    const to = "email@example.com"; // Default alert email, user can change
    const subject = `Alert from ${notification.groupName || 'WhatsApp Group'} - ${notification.senderName || 'Alert'}`;
    const body = notification.message || `Alert Summary:\n\n${notification.originalMessage || 'A business account alert was received in the group.'}\n\nPlease review and respond as needed.`;

    return { to, subject, body };
  };

  const handleInputChange = (field, value) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Event handlers
  const handleSendEmail = async (e) => {
    e?.preventDefault?.();
    setIsSending(true);

    try {
      if (onSendEmail && notification?.id) {
        await onSendEmail(notification.id, 
          emailData.to, emailData.subject, emailData.body);
      }
      onClose();
    } catch (error) {
      console.error("Failed to send email:", error);
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to send email: " + error.message
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleSave = (e) => {
    if (isSending) return;
    handleSendEmail(e);
  };

  const renderFormField = (id, label, type, placeholder, value, field, required = false, rows = null) => (
    <div className="form-group">
      <label htmlFor={id}>{label} {required && '*'}</label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          rows={rows}
          required={required}
        />
      ) : (
        <input
          type={type}
          id={id}
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          required={required}
        />
      )}
    </div>
  );

  const renderEmailForm = () => (
    <form onSubmit={handleSendEmail} className="email-form" style={{ padding: "0" }}>
      {renderFormField("emailTo", "To", "email", "Enter recipient email", emailData.to, "to", true)}
      {renderFormField("emailSubject", "Subject", "text", "Enter email subject", emailData.subject, "subject", true)}
      {renderFormField("emailBody", "Message", "textarea", "Enter email message", emailData.body, "body", true, 12)}
    </form>
  );

  const renderContextItem = (label, value) => (
    <div className="context-item">
      <strong>{label}:</strong> {value}
    </div>
  );

  const renderNotificationContext = () => {
    if (!notification) return null;

    return (
      <div className="notification-context-wrapper">
        <div className="notification-context">
          <div className="context-header">
            <i className="fas fa-info-circle"></i>
            Alert Context
          </div>
          <div className="context-content">
            {renderContextItem("Summary", notification.message)}
            {notification.originalMessage && renderContextItem("Original Message", notification.originalMessage)}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      header={"Compose Email"}
      buttons={true}
      onSave={handleSave}
      saveLabel={isSending ? "Sending..." : "Send Email"}
      saveDisabled={isSending}
    >
      <div className="compose-email-modal">
        {renderNotificationContext()}
        {renderEmailForm()}
      </div>
    </Modal>
  );
};

export default ComposeEmailModal;
